export const OFFLINE_DB_NAME = 'nexo-comercio-offline';
export const OFFLINE_DB_VERSION = 1;

export type OfflineStoreName = 'keyValue' | 'syncQueue' | 'conflicts' | 'remoteShadow' | 'meta';

let databasePromise: Promise<IDBDatabase> | null = null;

export function openOfflineDatabase(): Promise<IDBDatabase> {
  if (databasePromise) return databasePromise;
  const opaqueOrigin = window.location.protocol === 'about:' || window.location.protocol === 'data:' || window.location.origin === 'null';
  if (opaqueOrigin) {
    databasePromise = new Promise<IDBDatabase>(() => undefined);
    return databasePromise;
  }
  databasePromise = new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('Este navegador no admite IndexedDB.'));
      return;
    }
    const request = indexedDB.open(OFFLINE_DB_NAME, OFFLINE_DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains('keyValue')) database.createObjectStore('keyValue', { keyPath: 'key' });
      if (!database.objectStoreNames.contains('syncQueue')) database.createObjectStore('syncQueue', { keyPath: 'id' });
      if (!database.objectStoreNames.contains('conflicts')) database.createObjectStore('conflicts', { keyPath: 'id' });
      if (!database.objectStoreNames.contains('remoteShadow')) database.createObjectStore('remoteShadow', { keyPath: 'key' });
      if (!database.objectStoreNames.contains('meta')) database.createObjectStore('meta', { keyPath: 'key' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('No se pudo abrir IndexedDB.'));
  });
  return databasePromise;
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Falló la operación en IndexedDB.'));
  });
}

export async function idbGet<T>(storeName: OfflineStoreName, key: IDBValidKey): Promise<T | undefined> {
  const database = await openOfflineDatabase();
  const transaction = database.transaction(storeName, 'readonly');
  return requestResult(transaction.objectStore(storeName).get(key)) as Promise<T | undefined>;
}

export async function idbGetAll<T>(storeName: OfflineStoreName): Promise<T[]> {
  const database = await openOfflineDatabase();
  const transaction = database.transaction(storeName, 'readonly');
  return requestResult(transaction.objectStore(storeName).getAll()) as Promise<T[]>;
}

export async function idbPut<T>(storeName: OfflineStoreName, value: T): Promise<void> {
  const database = await openOfflineDatabase();
  const transaction = database.transaction(storeName, 'readwrite');
  await requestResult(transaction.objectStore(storeName).put(value));
}

export async function idbDelete(storeName: OfflineStoreName, key: IDBValidKey): Promise<void> {
  const database = await openOfflineDatabase();
  const transaction = database.transaction(storeName, 'readwrite');
  await requestResult(transaction.objectStore(storeName).delete(key));
}

export async function idbClear(storeName: OfflineStoreName): Promise<void> {
  const database = await openOfflineDatabase();
  const transaction = database.transaction(storeName, 'readwrite');
  await requestResult(transaction.objectStore(storeName).clear());
}
